import { Button } from "@au-schein-de/au-ui-library";
import { useTranslation } from "react-i18next";
import { TransformedImage } from "~/components/transformed-image.component";
import { COURIER_DELIVERY_ILLUSTRATION_SRC } from "~/constants/courier-delivery-illustration";

type MapNoAddressProps = {
	onEnterAddress?: () => void;
	/** "panel" = desktop map panel (centered); "card" = mobile white card. */
	variant?: "panel" | "card";
};

/**
 * Empty state shown in the courier/pickup map area when no address (postal
 * code) is set yet — a delivery illustration, prompt, and a CTA that focuses
 * the address/search input.
 */
export function MapNoAddress({
	onEnterAddress,
	variant = "panel",
}: MapNoAddressProps) {
	const { t } = useTranslation("product");
	const title = t("vendor.map.noAddress.title");
	const cta = t("vendor.map.noAddress.cta");

	if (variant === "card") {
		return (
			<div
				className="flex items-start gap-3 rounded-xl bg-white py-4 px-3"
				data-testid="map-no-address"
			>
				<TransformedImage
					src={COURIER_DELIVERY_ILLUSTRATION_SRC}
					alt=""
					width={48}
					height={48}
					loading="lazy"
					className="size-12 shrink-0 object-contain"
				/>
				<div className="flex min-w-0 flex-1 flex-col gap-3">
					<p className="text-body-bold text-gray-700">{title}</p>
					<button
						type="button"
						className="w-fit rounded-xl bg-ansay-light px-4 py-3 text-body-emphasis text-gray-700 transition-opacity hover:opacity-90"
						onClick={onEnterAddress}
						data-testid="map-no-address-cta"
					>
						{cta}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			className="flex h-full w-full items-center justify-center bg-gray-50 px-6 py-8"
			data-testid="map-no-address"
		>
			<div className="flex max-w-[320px] flex-col items-center gap-6 text-center">
				<TransformedImage
					src={COURIER_DELIVERY_ILLUSTRATION_SRC}
					alt=""
					width={80}
					height={80}
					loading="lazy"
					className="size-20 shrink-0 object-contain"
				/>
				<p className="text-h5 text-gray-700">{title}</p>
				<Button
					type="button"
					variant="secondary"
					className="w-full"
					onClick={onEnterAddress}
					data-testid="map-no-address-cta"
				>
					{cta}
				</Button>
			</div>
		</div>
	);
}
